import React, { useState, useEffect } from 'react';
import { X, ChevronLeft, ChevronRight, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';

const galleryImages = Array.from({ length: 30 }, (_, i) => ({
  id: i + 1,
  src: `/images/gallery/sq_img${String(i + 1).padStart(2, '0')}.jpg`,
  alt: `Chef Francesco at Event ${i + 1}`
}));

const chefBio = {
  name: "Chef Francesco Romano",
  title: "Corporate Chef & Culinary Entrepreneur",
  bio: `Chef Francesco Romano is a seasoned and innovative corporate chef with deep expertise in culinary leadership and brand development. With a passion for elevating dining experiences across diverse formats and audiences, Francesco has built a distinguished career blending creativity, operational excellence, and entrepreneurial vision.

He is the founder of ACtaly, Thunder Road, and CRISPYzza — brands recognized for their forward-thinking concepts and commitment to quality. Through these ventures, Chef Romano has demonstrated a rare ability to translate culinary trends into successful, sustainable business models while maintaining a strong emphasis on taste, presentation, and guest experience.

In addition to his entrepreneurial achievements, Chef Romano serves as a brand ambassador for Grana Padano in the UAE, where he champions the iconic Italian cheese through curated events, signature recipes, and educational initiatives that celebrate its heritage and culinary versatility.

Known for his leadership, creativity, and dedication to excellence, Chef Francesco Romano continues to influence the food scene with innovative concepts and authentic culinary storytelling.`,
  achievements: [
    "Founder of ACtaly, Thunder Road & CRISPYzza",
    "Brand Ambassador for Grana Padano in UAE",
    "Culinary Leadership Expert",
    "Award-Winning Restaurant Concepts"
  ]
};

const ChefGallery = () => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Scroll to top when page loads
    window.scrollTo(0, 0);
    setIsVisible(true);
  }, []);

  const openLightbox = (index) => {
    setSelectedImage(index);
    document.body.style.overflow = 'hidden';
  };

  const closeLightbox = () => {
    setSelectedImage(null);
    document.body.style.overflow = 'auto';
  };

  const goToPrevious = () => {
    setSelectedImage((prev) => (prev === 0 ? galleryImages.length - 1 : prev - 1));
  };

  const goToNext = () => {
    setSelectedImage((prev) => (prev === galleryImages.length - 1 ? 0 : prev + 1));
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (selectedImage === null) return;
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowLeft') goToPrevious();
      if (e.key === 'ArrowRight') goToNext();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedImage]);

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white border-b border-[#e8e3db] sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Link 
              to="/" 
              className="flex items-center gap-2 text-[#1e1919] hover:text-[#c9a962] transition-colors"
            >
              <ArrowLeft size={20} />
              <span className="font-medium">Back to Home</span>
            </Link>
            <img
              src="/images/logo_final.jpeg"
              alt="CRISPYzza"
              className="h-10 w-auto object-contain"
            />
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative py-16 lg:py-24 bg-[#1e1919] overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
              backgroundSize: '40px 40px',
            }}
          />
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left - Bio */}
            <div className={`transition-all duration-1000 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`}>
              <span className="text-[#c9a962] text-sm font-semibold tracking-[0.2em] uppercase">
                Meet The Chef
              </span>
              <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-white mt-4 mb-2">
                {chefBio.name}
              </h1>
              <p className="text-xl text-[#c9a962] font-medium mb-8">
                {chefBio.title}
              </p>
              
              <div className="prose prose-lg text-[#bbb5ae] leading-relaxed">
                {chefBio.bio.split('\n\n').map((paragraph, index) => (
                  <p key={index} className="mb-4">{paragraph}</p>
                ))}
              </div>

              {/* Achievements */}
              <div className="mt-8 grid grid-cols-2 gap-4">
                {chefBio.achievements.map((achievement, index) => (
                  <div 
                    key={index}
                    className="flex items-center gap-3 bg-white/5 rounded-lg p-3"
                  >
                    <div className="w-2 h-2 bg-[#c9a962] rounded-full" />
                    <span className="text-white text-sm">{achievement}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Right - Featured Image */}
            <div className={`transition-all duration-1000 delay-300 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'}`}>
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-br from-[#c9a962]/20 to-transparent rounded-3xl blur-xl" />
                <img
                  src="/images/gallery/sq_img01.jpg"
                  alt="Chef Francesco Romano"
                  className="relative rounded-2xl w-full aspect-square object-cover shadow-2xl"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-[#c9a962] text-sm font-semibold tracking-[0.2em] uppercase">
              Photo Gallery
            </span>
            <h2 className="font-display text-3xl md:text-4xl font-bold text-[#1e1919] mt-4">
              Chef Francesco at Major Events
            </h2>
            <p className="text-[#736c64] mt-4 max-w-2xl mx-auto">
              From the Italian Embassy to international food exhibitions, explore Chef Francesco's journey through prestigious culinary events across the UAE and beyond.
            </p>
          </div>

          {/* Gallery Grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
            {galleryImages.map((image, index) => (
              <div
                key={image.id}
                className={`group cursor-pointer overflow-hidden rounded-xl aspect-square transition-all duration-500 ${
                  isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                }`}
                style={{ transitionDelay: `${Math.min(index * 50, 500)}ms` }}
                onClick={() => openLightbox(index)}
              >
                <img
                  src={image.src}
                  alt={image.alt}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-[#1e1919]/0 group-hover:bg-[#1e1919]/30 transition-colors duration-300" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Lightbox */}
      {selectedImage !== null && (
        <div 
          className="fixed inset-0 z-50 bg-[#1e1919]/95 backdrop-blur-sm flex items-center justify-center"
          onClick={closeLightbox}
        >
          {/* Close Button */}
          <button
            className="absolute top-6 right-6 text-white/80 hover:text-white transition-colors z-50"
            onClick={closeLightbox}
          >
            <X size={32} />
          </button>

          {/* Previous Button */}
          <button
            className="absolute left-4 md:left-8 text-white/80 hover:text-white transition-colors p-2 bg-white/10 rounded-full hover:bg-white/20"
            onClick={(e) => {
              e.stopPropagation();
              goToPrevious();
            }}
          >
            <ChevronLeft size={32} />
          </button>

          {/* Image */}
          <div 
            className="max-w-4xl max-h-[80vh] mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={galleryImages[selectedImage].src}
              alt={galleryImages[selectedImage].alt}
              className="max-w-full max-h-[80vh] object-contain rounded-lg shadow-2xl"
            />
            <p className="text-center text-white/60 mt-4">
              {selectedImage + 1} / {galleryImages.length}
            </p>
          </div>

          {/* Next Button */}
          <button
            className="absolute right-4 md:right-8 text-white/80 hover:text-white transition-colors p-2 bg-white/10 rounded-full hover:bg-white/20"
            onClick={(e) => {
              e.stopPropagation();
              goToNext();
            }}
          >
            <ChevronRight size={32} />
          </button>
        </div>
      )}
    </div>
  );
};

export default ChefGallery;
